import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Eye,
  Receipt,
  Calendar,
  Banknote,
  Landmark
} from 'lucide-react';
import { saleReturnsAPI, salesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import DataTable from '../ui/DataTable';
import { readParam, useSyncUrlParams } from '../../hooks/useUrlListState';

const SaleReturnList = () => {
  const navigate = useNavigate();
  // View state (search/pagination) is seeded from the URL so the list is
  // shareable and restored when returning from a return's detail page.
  const [searchParams] = useSearchParams();
  const [saleReturns, setSaleReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  // draftSearch = what's typed in the box; searchTerm = the applied query. They
  // diverge until the user hits Search / Enter, so nothing loads as you type.
  const [draftSearch, setDraftSearch] = useState(() => readParam(searchParams, 'q', ''));
  const [searchTerm, setSearchTerm] = useState(() => readParam(searchParams, 'q', ''));
  const [isSearching, setIsSearching] = useState(() => Boolean(readParam(searchParams, 'q', '')));
  const [searchResults, setSearchResults] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [sortBy, setSortBy] = useState(() => readParam(searchParams, 'sort', 'ID'));
  const [sortOrder, setSortOrder] = useState(() => readParam(searchParams, 'order', 'desc'));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(() => readParam(searchParams, 'page', 1, 'number'));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => readParam(searchParams, 'size', 20, 'number'));

  // Mirror the current view into the URL (defaults omitted).
  useSyncUrlParams(
    { q: searchTerm.trim(), sort: sortBy, order: sortOrder, page: currentPage, size: itemsPerPage },
    { q: '', sort: 'ID', order: 'desc', page: 1, size: 20 },
  );
  // Skip the first run of the applied-change / sort effects so restored state
  // isn't clobbered on mount (the initial load below handles it).
  const appliedInitRef = React.useRef(false);
  const sortInitRef = React.useRef(false);
  

  
  const { formatPrice } = useCurrency();

  // Column definitions for DataTable
  const columns = [
    {
      key: 'ID',
      title: 'ID',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-900">Ret-{value}</span>
        </div>
      )
    },
    {
      key: 'Source',
      title: 'Source',
      sortable: false,
      render: (value, row) => (
        row.woocommerce_id ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">WEB</span>
            <span className="text-sm text-gray-900">#{row.woocommerce_id}{row.brand ? ` (${row.brand})` : ''}</span>
            {(row.wc_sync_status === 'synced' || row.wc_sync_status === 'failed') && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                row.wc_sync_status === 'synced'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>{row.wc_sync_status}</span>
            )}
          </span>
        ) : row.Exchange_ID ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">EXCHANGE</span>
            <span className="text-sm text-gray-900">{row.exchange?.ExchangeNo || `#${row.Exchange_ID}`}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">SALE</span>
            <span className="text-sm text-gray-900">{row.sale?.SaleNo || (row.Sale_ID ? `#${row.Sale_ID}` : '—')}</span>
          </span>
        )
      )
    },
    {
      key: 'CustomerName',
      title: 'Customer Name',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{(row?.exchange || row?.sale)?.CustomerName || '—'}</span>
        </div>
      )
    },
    {
      key: 'CustomerPhone',
      title: 'Customer Phone',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{row?.sale?.CustomerPhone}</span>
        </div>
      )
    },
    {
      key: 'SaleReturnDate',
      title: 'Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <div>
            <div className="text-sm text-gray-900">
              {new Date(value).toLocaleDateString()}
            </div>
            <div className="text-xs text-gray-500">
              {new Date(value).toLocaleTimeString()}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'PaymentMethod',
      title: 'Refunded Via',
      sortable: true,
      render: (value) => {
        const isBank = value === 'bank';
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              isBank ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {isBank ? <Landmark className="h-3 w-3" /> : <Banknote className="h-3 w-3" />}
            {isBank ? 'Bank' : 'Cash'}
          </span>
        );
      }
    },
    {
      key: 'Remarks',
      title: 'Remarks',
      sortable: false,
      render: (value) => (
        <span className="text-sm text-gray-500">
          {value || '-'}
        </span>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewSaleReturn(row)}
            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];


  useEffect(() => {
    // Initial load respects the search/page restored from the URL.
    if (searchTerm.trim()) {
      handleRealTimeSearch();
    } else {
      loadData(currentPage);
    }
  }, []);

  // Reload when the APPLIED search changes (Search button / Enter / Clear), not
  // as you type. Skips the first run so the mount load above isn't duplicated.
  useEffect(() => {
    if (!appliedInitRef.current) {
      appliedInitRef.current = true;
      return;
    }
    setCurrentPage(1);
    if (searchTerm.trim()) {
      handleRealTimeSearch();
    } else {
      setIsSearching(false);
      setSearchResults([]);
      loadData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Re-query the current page when the sort changes (server-side sort).
  useEffect(() => {
    if (!sortInitRef.current) {
      sortInitRef.current = true;
      return;
    }
    if (searchTerm.trim()) {
      handleRealTimeSearch();
    } else {
      loadData(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  const loadData = async (page = currentPage, limit = itemsPerPage) => {
    try {
      setLoading(true);
      const skip = (page - 1) * limit;
      const response = await saleReturnsAPI.list({ skip, limit, sort: sortBy, order: sortOrder });
      setSaleReturns(response.data);
      
      if (response.data.length === limit) {
        setTotalPages(page + 1);
      } else {
        setTotalPages(page);
      }
      setTotalItems((page - 1) * limit + response.data.length);
    } catch (error) {
      console.error('Error loading sale returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRealTimeSearch = async () => {
    if (!searchTerm.trim()) return;
    
    try {
      setIsSearching(true);
      const response = await saleReturnsAPI.list({ limit: 100, search: searchTerm, sort: sortBy, order: sortOrder });
      console.log('Search response:', response.data); // Debug log
      setSearchResults(response.data);
      setSaleReturns(response.data);
      setTotalPages(1);
      
      if (!searchHistory.includes(searchTerm)) {
        setSearchHistory(prev => [searchTerm, ...prev.slice(0, 4)]);
      }
    } catch (error) {
      console.error('Error searching sale returns:', error);
    } finally {
      // Clear the initial page spinner when the first load is a restored search
      // (loadData isn't the one that runs in that case).
      setLoading(false);
    }
  };

  // Apply the typed search (Search button / Enter). The applied-change effect
  // above does the fetch and page reset — nothing loads as you type.
  const handleSearch = () => {
    setSearchTerm(draftSearch);
  };

  const clearSearch = () => {
    setDraftSearch('');
    setSearchTerm('');
  };

  const handleDeleteSaleReturn = async (id) => {
    if (window.confirm('Are you sure you want to delete this sale return?')) {
      try {
        await saleReturnsAPI.remove(id);
        loadData();
      } catch (error) {
        console.error('Error deleting sale return:', error);
      }
    }
  };

  const handleViewSaleReturn = (saleReturn) => {
    navigate(`/sale-return/${saleReturn.ID}`);
  };

  const handleCreateSaleReturn = () => {
    navigate('/sale-return/new');
  };

  const handleEditSaleReturn = (saleReturn) => {
    navigate(`/sale-return/edit/${saleReturn.ID}`);
  };


  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadData(page);
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadData(1, newItemsPerPage);
  };

  // Sorting is server-side: set the new sort; the sort effect re-queries.
  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sale Returns Management</h1>
          <p className="text-gray-600">Manage sale returns with comprehensive tracking and monitoring</p>
        </div>
        <button
          onClick={handleCreateSaleReturn}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          New Sale Return
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        {/* Main Search Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by ID, remarks or customer name or phone number..."
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              style={{ fontSize: '1rem' }}
              className="w-full h-12 pl-11 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {draftSearch && (
              <button
                onClick={clearSearch}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="h-12 px-6 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium whitespace-nowrap"
          >
            <Search className="h-5 w-5" />
            Search
          </button>
        </div>

      </div>

      {/* Search Results Indicator */}
      {isSearching && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-blue-800">
              Showing {saleReturns.length} search results for "{searchTerm}"
            </p>
            <button
              onClick={clearSearch}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Clear Search
            </button>
          </div>
        </div>
      )}

      {/* Sale Returns Table */}
      <DataTable
        columns={columns}
        data={saleReturns}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={saleReturns.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        itemLabel="sale returns"
        onRowClick={(row, e) =>
          e.metaKey || e.ctrlKey
            ? window.open(`/sale-return/${row.ID}`, '_blank', 'noopener')
            : navigate(`/sale-return/${row.ID}`)
        }
      />


    </div>
  );
};

export default SaleReturnList;
